import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Instagram, ExternalLink, Heart, MessageCircle, Loader } from 'lucide-react';

interface InstagramPost {
  id: string;
  media_url: string;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  caption?: string;
  permalink: string;
  timestamp: string;
  like_count?: number;
  comments_count?: number;
}

const InstagramFeed: React.FC = () => {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Placeholder data yang realistis untuk TN Cakes Bali
  const placeholderPosts = [
    {
      id: '1',
      media_url: '/tncakes/WeddingCakes/WeddingCakes3.jpeg',
      media_type: 'IMAGE' as const,
      caption: 'Elegant 3-tier wedding cake with fresh roses 🌹 Perfect for your special day! #TNCakesBali #WeddingCakes #BaliCakes',
      permalink: 'https://instagram.com/tncakesbali',
      timestamp: '2025-01-28T10:30:00Z',
      like_count: 127,
      comments_count: 15
    },
    {
      id: '2',
      media_url: '/tncakes/BirthdayCakes/BirthdayCakes5.jpeg',
      media_type: 'IMAGE' as const,
      caption: 'Princess castle birthday cake 🏰✨ Making dreams come true, one cake at a time! #BirthdayCakes #PrincessCake #CustomCakes',
      permalink: 'https://instagram.com/tncakesbali',
      timestamp: '2025-01-27T14:20:00Z',
      like_count: 89,
      comments_count: 8
    },
    {
      id: '3',
      media_url: '/tncakes/CelebrationCakes/CelebrationCakes4.jpeg', // Using celebration instead, no baby shower right now
      media_type: 'IMAGE' as const,
      caption: 'Adorable baby shower cake 👶💙 Celebrating new life with sweet moments! #BabyShower #CustomCakes #BaliCakeDesigner',
      permalink: 'https://instagram.com/tncakesbali',
      timestamp: '2025-01-26T16:45:00Z',
      like_count: 156,
      comments_count: 22
    },
    {
      id: '4',
      media_url: '/tncakes/Cupcakes/Cupcakes3.jpeg',
      media_type: 'IMAGE' as const,
      caption: 'Premium cupcake collection 🧁 Perfect for any celebration! #Cupcakes #PremiumDesserts #HandmadeWithLove',
      permalink: 'https://instagram.com/tncakesbali',
      timestamp: '2025-01-25T11:30:00Z',
      like_count: 94,
      comments_count: 12
    },
    {
      id: '5',
      media_url: '/tncakes/CorporateCakes/CorporateCakes3.jpeg',
      media_type: 'IMAGE' as const,
      caption: 'Corporate event cake for product launch 🎊 Professional designs for business celebrations! #CorporateCakes #EventCakes',
      permalink: 'https://instagram.com/tncakesbali',
      timestamp: '2025-01-24T09:15:00Z',
      like_count: 73,
      comments_count: 6
    },
    {
      id: '6',
      media_url: '/tncakes/WeddingCakes/WeddingCakes4.jpeg',
      media_type: 'IMAGE' as const,
      caption: 'Minimalist wedding cake with gold accents ✨ Simple elegance at its finest! #MinimalistCake #GoldAccents #ElegantDesign',
      permalink: 'https://instagram.com/tncakesbali',
      timestamp: '2025-01-23T13:20:00Z',
      like_count: 201,
      comments_count: 28
    }
  ];

  useEffect(() => {
    // Simulate API call to Instagram
    const fetchInstagramPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        // TODO: Replace with actual Instagram API call
        // const response = await fetch('/api/instagram/posts');
        // const data = await response.json();

        // Simulate loading delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        // For now, use placeholder data
        setPosts(placeholderPosts);
      } catch (err) {
        setError('Failed to load Instagram posts');
        setPosts(placeholderPosts); // Fallback to placeholder
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramPosts();
  }, []);

  if (loading) {
    return (
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-900 mb-4">
              Latest from Instagram
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full mx-auto mb-4"></div>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-6">
              Follow our journey and see our latest cake creations on Instagram
            </p>
          </div>

          <div className="flex items-center justify-center min-h-[200px]">
            <div className="text-center">
              <Loader className="w-6 h-6 animate-spin text-pink-500 mx-auto mb-3" />
              <p className="text-gray-600 text-sm">Loading latest posts from @tncakesbali...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-900 mb-4">
            Latest from Instagram
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full mx-auto mb-4"></div>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-6">
            Follow our journey and see our latest cake creations on Instagram
          </p>
          <a
            href="https://www.instagram.com/tncakesbali"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-5 py-2.5 rounded-full font-semibold hover:from-pink-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg text-sm"
          >
            <Instagram className="w-4 h-4" />
            Follow @tncakesbali
            <ExternalLink className="w-3 h-3" />
          </a>
        </motion.div>

        {error && (
          <div className="text-center mb-4">
            <p className="text-red-600 bg-red-50 px-3 py-2 rounded-lg inline-block text-sm">
              {error}
            </p>
          </div>
        )}

        {/* Instagram Grid - More Compact */}
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
          {posts.slice(0, 6).map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group cursor-pointer"
            >
              <a
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className="relative overflow-hidden rounded-lg aspect-square shadow-sm group-hover:shadow-md transition-all duration-300">
                  <div
                    className="h-full w-full bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundImage: `url(${post.media_url})` }}
                  />

                  {/* Minimal Hover Overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="flex items-center gap-2 text-white">
                        <div className="flex items-center gap-1">
                          <Heart className="w-2.5 h-2.5" />
                          <span className="text-xs font-medium">{post.like_count}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageCircle className="w-2.5 h-2.5" />
                          <span className="text-xs font-medium">{post.comments_count}</span>
                        </div>
                      </div>
                    </div>

                    {/* Minimal Instagram Icon */}
                    <div className="absolute top-2 right-2">
                      <Instagram className="w-3 h-3 text-white" />
                    </div>
                  </div>
                </div>
              </a>
            </motion.div>
          ))}
        </div>

        {/* Compact View More Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-4"
        >
          <a
            href="https://www.instagram.com/tncakesbali"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-pink-600 font-medium hover:text-pink-700 transition-colors border border-pink-200 hover:border-pink-300 px-4 py-2 rounded-full text-xs"
          >
            View More on Instagram
            <ExternalLink className="w-3 h-3" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default InstagramFeed;
